/**
 * Products list page. Group master-detail layout (spec 0103, replacing the 0060
 * collapsible accordion folders): a product-group nav-list on the left, the
 * products table on the right, with an "All" entry selected by default so every
 * product shows until a group is picked. The selection is URL-reflected via the
 * `?group=<name>` search param — absent means "All" — matching Tenants, so
 * browser back/forward and deep-links work. Grouping is client-side over the
 * already-loaded `GET /products` payload.
 */
import {
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useMemo } from 'react';
import {
  Link as RouterLink,
  useLoaderData,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { apiAdmin } from '@/api/admin';
import type { ProductDto } from '@/api/types';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { GroupMasterDetail } from '@/components/GroupMasterDetail';
import { PageHeader } from '@/components/PageHeader';
import { useCopy } from '@/hooks/useCopy';

export interface ProductsListLoaderData {
  products: ProductDto[];
}

export async function productsListLoader(): Promise<ProductsListLoaderData> {
  const products = await apiAdmin.products.list();
  return { products };
}

const MFA_COLORS: Record<string, 'error' | 'warning' | 'default'> = {
  REQUIRED: 'error',
  OPTIONAL: 'warning',
  DISABLED: 'default',
  ALWAYS: 'error',
};

export function ProductsList() {
  const { products } = useLoaderData() as ProductsListLoaderData;
  const { t } = useCopy();
  const navigate = useNavigate();
  // Spec 0103 — the selected group lives in the URL (`?group=<name>`) so
  // browser navigation/back and deep-links work. No param = "All".
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedGroup = searchParams.get('group');

  // Group the products by `productGroup`, groups sorted by name — the nav-list
  // rows and their counts. The field is on every row and the set is small, so
  // this is all client-side.
  const sections = useMemo(() => {
    const byGroup = new Map<string, ProductDto[]>();
    for (const p of products) {
      const arr = byGroup.get(p.productGroup);
      if (arr) arr.push(p);
      else byGroup.set(p.productGroup, [p]);
    }
    return [...byGroup.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([group, items]) => ({ group, items }));
  }, [products]);

  // The right pane's rows: the selected group's products, or every product
  // (in group order, as the accordion presented them) when "All" is selected.
  const visible = useMemo(() => {
    if (selectedGroup == null) return sections.flatMap((s) => s.items);
    return sections.find((s) => s.group === selectedGroup)?.items ?? [];
  }, [sections, selectedGroup]);

  // `null` selects the "All" row, which clears the param rather than setting it.
  function selectGroup(group: string | null) {
    setSearchParams(group == null ? {} : { group });
  }

  return (
    <Box>
      <Breadcrumbs
        items={[
          { label: t('nav.home', 'Home'), to: '/' },
          { label: t('nav.reference', 'Reference') },
          { label: t('nav.products', 'Products') },
        ]}
      />
      <PageHeader
        title={t('products.list.title', 'Products')}
        subtitle={t('products.list.subtitle', '')}
        actions={
          <Button
            component={RouterLink}
            to="/reference/products/new"
            variant="contained"
            startIcon={<AddIcon />}
          >
            {t('products.list.create', 'New product')}
          </Button>
        }
      />

      <GroupMasterDetail
        groups={sections.map((s) => ({ key: s.group, label: s.group, count: s.items.length }))}
        selectedKey={selectedGroup}
        onSelect={selectGroup}
        allLabel={t('products.list.allGroups', 'All products')}
        allCount={products.length}
      >
        {visible.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
            <Typography sx={{ color: 'text.secondary' }}>
              {selectedGroup != null
                ? t('products.list.emptyFiltered', 'No products in this group.')
                : t('products.list.empty', 'No products yet.')}
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('products.list.columns.displayName', 'Name')}</TableCell>
                  <TableCell>{t('products.list.columns.slug', 'Slug')}</TableCell>
                  <TableCell>{t('products.list.columns.jwtAudience', 'JWT audience')}</TableCell>
                  <TableCell>{t('products.list.columns.defaultMfaPolicy', 'MFA')}</TableCell>
                  <TableCell>{t('products.list.columns.appTopology', 'Packaging')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.map((p) => (
                  <TableRow
                    key={p.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/reference/products/${p.id}`)}
                  >
                    <TableCell>{p.displayName}</TableCell>
                    <TableCell>{p.slug}</TableCell>
                    <TableCell>{p.jwtAudience}</TableCell>
                    <TableCell>
                      <Chip
                        label={t(`products.mfa.${p.defaultMfaPolicy}`, p.defaultMfaPolicy)}
                        color={MFA_COLORS[p.defaultMfaPolicy] ?? 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {p.appTopology === 'SELF_CONTAINED' ? (
                        <Chip
                          label={t('products.packaging.SELF_CONTAINED', 'Self-contained')}
                          size="small"
                          color="default"
                        />
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </GroupMasterDetail>
    </Box>
  );
}
